// Veículos: caminhões (tratores) com placa licenciada e carretas acopladas.
import {
  categorias, colaboradores, despesas, licencas, parcelas, situacoesDeEnvio, veiculos,
} from '../dados.js';
import { consumoMedio, somarDias } from '../lib/despesas.js';
import { consumos, cartaoDespesa } from './despesas.js';
import { emblemaEnvio } from '../ui/sinal.js';
import { h, trocar } from '../lib/dom.js';
import { mensagemDeErro } from '../lib/erros.js';
import { data, lerInteiro, litros as formatarLitros, numeroBR, placa as formatarPlaca, plural } from '../lib/formato.js';
import { porVeiculo, situacaoDaPlaca } from '../lib/licencas.js';
import { precoPorMes } from '../lib/preco.js';
import { EIXOS } from '../lib/antt.js';
import { CARROCERIAS } from '../lib/trajeto.js';
import { apenasDigitos, normalizarPlaca, placaValida, renavamValido } from '../lib/validacao.js';
import { nestaTela } from '../rotas.js';
import { ehTitular, hojeDaEmpresa, podeGravar, contaGravavel } from '../sessao.js';
import { casca } from '../ui/casca.js';
import {
  avisar, campo, carregando, cartaoLink, confirmar, dialogo, emblema, faixa, formulario, secao, vazio,
} from '../ui/componentes.js';
import { faixaDaConta } from './inicio.js';
import { blocoManutencaoDoVeiculo } from './manutencao.js';

const NOME_TIPO = { trator: 'Caminhão', implemento: 'Carreta' };

function descricao(v) {
  return [v.marca, v.modelo, v.ano].filter(Boolean).join(' ');
}

function emblemaPlaca(situacao) {
  if (situacao.ativa && situacao.encerraHoje) return emblema('Placa ativa até hoje', 'alerta');
  if (situacao.ativa) return emblema('Placa ativa', 'ok');
  return emblema('Sem placa ativa');
}

function tituloVeiculo(v) {
  return h('span', null, h('span', { class: 'placa-texto' }, formatarPlaca(v.placa)), v.apelido ? ` · ${v.apelido}` : '');
}

// ---------------------------------------------------------------------
// Lista
// ---------------------------------------------------------------------
export function telaVeiculos() {
  const corpo = h('div', { class: 'secao' }, carregando());

  (async () => {
    const [lista, lics, envio] = await Promise.all([veiculos.listar(), licencas.listar(), situacoesDeEnvio('veiculos')]);
    const hoje = hojeDaEmpresa();
    const porV = porVeiculo(lics);
    const tratoresAtivos = lista.filter((v) => v.tipo === 'trator' && v.ativo);
    const idsTratoresAtivos = new Set(tratoresAtivos.map((t) => t.id));
    const carretasAtivas = lista.filter((v) => v.tipo === 'implemento' && v.ativo);
    const soltas = carretasAtivas.filter((c) => !idsTratoresAtivos.has(c.acoplado_a));
    const inativos = lista.filter((v) => !v.ativo);
    const gravavel = podeGravar();

    if (lista.length === 0) {
      trocar(corpo, vazio({
        simbolo: 'caminhao',
        titulo: 'Nenhum veículo ainda',
        texto: 'Comece pelo caminhão (cavalo). Depois você cadastra as carretas e acopla em cada um.',
        acao: gravavel && h('a', { class: 'botao', href: '#/veiculos/novo?tipo=trator' }, 'Cadastrar caminhão'),
      }));
      return;
    }

    const cartoesTratores = tratoresAtivos.map((t) => {
      const situacao = situacaoDaPlaca(porV[t.id] ?? [], hoje);
      const minhas = carretasAtivas.filter((c) => c.acoplado_a === t.id);
      return h('div', null,
        cartaoLink({
          href: `#/veiculos/${t.id}`,
          simbolo: 'caminhao',
          titulo: tituloVeiculo(t),
          linhas: [descricao(t)],
          emblemas: [emblemaPlaca(situacao), emblemaEnvio(envio.get(t.id))],
        }),
        minhas.length > 0 && h('div', { class: 'sublista' }, minhas.map((c) =>
          cartaoLink({ href: `#/veiculos/${c.id}`, simbolo: 'carreta', titulo: tituloVeiculo(c), linhas: [descricao(c) || 'Carreta'], emblemas: [emblemaEnvio(envio.get(c.id))] }))));
    });

    const comPlaca = tratoresAtivos.filter((t) => situacaoDaPlaca(porV[t.id] ?? [], hoje).ativa).length;

    trocar(corpo, 
      gravavel && h('div', { class: 'acoes-lado' },
        h('a', { class: 'botao', href: '#/veiculos/novo?tipo=trator' }, '+ Caminhão'),
        tratoresAtivos.length > 0 && h('a', { class: 'botao secundario', href: '#/veiculos/novo?tipo=implemento' }, '+ Carreta')),
      secao('Caminhões', h('div', { class: 'lista' }, cartoesTratores), {
        contador: `${plural(comPlaca, 'placa ativa', 'placas ativas')}`,
      }),
      soltas.length > 0 && secao('Carretas sem caminhão ativo', h('div', { class: 'lista' }, soltas.map((c) =>
        cartaoLink({ href: `#/veiculos/${c.id}`, simbolo: 'carreta', titulo: tituloVeiculo(c), linhas: ['Acople num caminhão ativo para lançar despesas'], emblemas: [emblema('Sem caminhão', 'alerta'), emblemaEnvio(envio.get(c.id))] })))),
      inativos.length > 0 && secao('Desativados', h('div', { class: 'lista' }, inativos.map((v) =>
        cartaoLink({ href: `#/veiculos/${v.id}`, simbolo: v.tipo === 'trator' ? 'caminhao' : 'carreta', titulo: tituloVeiculo(v), linhas: [NOME_TIPO[v.tipo]], emblemas: [emblema('Desativado'), emblemaEnvio(envio.get(v.id))] })))),
    );
  })().catch((erro) => {
    console.error(erro);
    trocar(corpo, faixa(mensagemDeErro(erro), 'perigo'));
  });

  return casca({ titulo: 'Veículos', conteudo: [faixaDaConta(), corpo] });
}

// ---------------------------------------------------------------------
// Cadastro e edição
// ---------------------------------------------------------------------
export function telaVeiculo({ params, busca }) {
  const tela = nestaTela();
  const novo = !params.id;
  const corpo = h('div', { class: 'secao' }, carregando());
  const titulo = h('span', null, novo ? (busca.get('tipo') === 'implemento' ? 'Nova carreta' : 'Novo caminhão') : 'Veículo');

  (async () => {
    const [lista, lics] = await Promise.all([veiculos.listar(), licencas.listar()]);
    const v = novo ? null : lista.find((x) => x.id === params.id) ?? (await veiculos.obter(params.id));
    if (!novo && !v) {
      trocar(corpo, vazio({ titulo: 'Veículo não encontrado', texto: 'Ele pode ter sido excluído.', acao: h('a', { class: 'botao', href: '#/veiculos' }, 'Ver veículos') }));
      return;
    }
    const tipo = v?.tipo ?? (busca.get('tipo') === 'implemento' ? 'implemento' : 'trator');
    titulo.textContent = novo ? (tipo === 'implemento' ? 'Nova carreta' : 'Novo caminhão') : `${NOME_TIPO[tipo]} ${formatarPlaca(v.placa)}`;
    const tratoresAtivos = lista.filter((x) => x.tipo === 'trator' && x.ativo && x.id !== v?.id);
    const gravavel = podeGravar();
    const hoje = hojeDaEmpresa();
    const porV = porVeiculo(lics);
    // Placa que já teve licença não muda: trocar de caminhão é pela "troca de placa".
    const placaTravada = !novo && tipo === 'trator' && (porV[v.id] ?? []).length > 0;

    if (novo && tipo === 'implemento' && tratoresAtivos.length === 0) {
      trocar(corpo, vazio({
        simbolo: 'carreta',
        titulo: 'Cadastre um caminhão primeiro',
        texto: 'A carreta sempre fica acoplada a um caminhão. É a placa do caminhão que conta na assinatura.',
        acao: h('a', { class: 'botao', href: '#/veiculos/novo?tipo=trator' }, 'Cadastrar caminhão'),
      }));
      return;
    }

    const campos = [
      campo({
        rotulo: 'Placa',
        nome: 'placa',
        obrigatorio: true,
        valor: v?.placa ?? '',
        maiusculas: true,
        formato: 'placa',
        dica: placaTravada ? 'A placa não muda depois de licenciada. Para pôr outro caminhão no lugar, use "Trocar por outro caminhão".' : null,
        atributos: { autocomplete: 'off', autocapitalize: 'characters', spellcheck: 'false', maxlength: '8', placeholder: 'ABC1D23', readonly: placaTravada || null },
      }),
      campo({ rotulo: 'Apelido', nome: 'apelido', valor: v?.apelido ?? '', dica: tipo === 'trator' ? 'Como vocês chamam o caminhão: "Scania", "Volvo branco"…' : 'Opcional: "Graneleira", "Sider"…' }),
      h('div', { class: 'grade-2' },
        campo({ rotulo: 'Marca', nome: 'marca', valor: v?.marca ?? '', formato: 'marca' }),
        campo({ rotulo: 'Modelo', nome: 'modelo', valor: v?.modelo ?? '' })),
      h('div', { class: 'grade-2' },
        campo({ rotulo: 'Ano', nome: 'ano', valor: v?.ano ?? '', formato: 'ano', atributos: { inputmode: 'numeric', maxlength: '4' } }),
        campo({ rotulo: 'RENAVAM', nome: 'renavam', valor: v?.renavam ?? '', formato: 'renavam', atributos: { inputmode: 'numeric', maxlength: '11' } })),
      tipo === 'trator' && h('div', { class: 'grade-2' },
        campo({ rotulo: 'Carroceria', nome: 'carroceria', tipo: 'select', valor: v?.carroceria ?? '',
          opcoes: [['', 'Não informada'], ...CARROCERIAS], dica: 'A da carreta que ele puxa (o Planejar o trajeto conta só a carga que ela leva).' }),
        campo({ rotulo: 'Eixos carregados', nome: 'eixos', tipo: 'select', valor: v?.eixos ? String(v.eixos) : '',
          opcoes: [['', 'Não informado'], ...EIXOS.map((n) => [String(n), `${n} eixos`])], dica: 'Caminhão + carreta (para o piso da ANTT).' })),
      // Modo viagem (0.23.0): o abastecimento planejado e os vencimentos.
      tipo === 'trator' && campo({
        rotulo: 'Tanque (litros)', nome: 'tanque_litros', formato: 'km', valor: v?.tanque_litros ? numeroBR(v.tanque_litros) : '',
        dica: 'Quantos litros cabem nos tanques (somados). O modo viagem diz onde abastecer.', atributos: { inputmode: 'numeric' },
      }),
      tipo === 'trator' && h('div', { class: 'grade-2' },
        campo({ rotulo: 'Licenciamento (CRLV) vale até', nome: 'licenciamento_ate', tipo: 'date', valor: v?.licenciamento_ate ?? '' }),
        campo({ rotulo: 'Seguro vale até', nome: 'seguro_ate', tipo: 'date', valor: v?.seguro_ate ?? '' })),
      tipo === 'trator'
        ? campo({
          rotulo: 'Hodômetro inicial (km)',
          nome: 'hodometro_inicial',
          formato: 'km',
          valor: v?.hodometro_inicial != null ? numeroBR(v.hodometro_inicial) : '',
          dica: 'Leitura do painel quando começou a usar o HSA Frota. É a base do custo por km.',
          atributos: { inputmode: 'numeric' },
        })
        : campo({
          rotulo: 'Acoplada ao caminhão',
          nome: 'acoplado_a',
          tipo: 'select',
          obrigatorio: true,
          valor: v?.acoplado_a ?? busca.get('caminhao') ?? (tratoresAtivos.length === 1 ? tratoresAtivos[0].id : ''),
          opcoes: [['', 'Escolha o caminhão'], ...tratoresAtivos.map((t) => [t.id, `${formatarPlaca(t.placa)}${t.apelido ? ` · ${t.apelido}` : ''}`])],
          dica: 'Cada caminhão aceita até 3 carretas.',
        }),
      campo({ rotulo: 'Observação', nome: 'observacao', tipo: 'textarea', valor: v?.observacao ?? '' }),
    ];

    const form = formulario({
      filhos: campos,
      textoBotao: novo ? 'Cadastrar' : 'Salvar alterações',
      somenteLeitura: !gravavel,
      validar: (d) => {
        const erros = {};
        if (!placaValida(d.placa)) erros.placa = 'Placa inválida. Use o formato ABC1D23 ou ABC1234.';
        if (d.ano && !(/^\d{4}$/.test(d.ano) && Number(d.ano) >= 1950 && Number(d.ano) <= 2100)) erros.ano = 'Ano com 4 números.';
        if (d.renavam && !renavamValido(d.renavam)) erros.renavam = 'O RENAVAM tem de 9 a 11 números.';
        if (tipo === 'trator' && d.hodometro_inicial && !(lerInteiro(d.hodometro_inicial) >= 0)) erros.hodometro_inicial = 'Só números, sem vírgula.';
        if (tipo === 'trator' && d.tanque_litros && !(lerInteiro(d.tanque_litros) >= 50 && lerInteiro(d.tanque_litros) <= 3000)) {
          erros.tanque_litros = 'Use de 50 a 3.000 litros.';
        }
        if (tipo === 'implemento' && !d.acoplado_a) erros.acoplado_a = 'Escolha o caminhão.';
        return erros;
      },
      aoEnviar: async (d) => {
        const dados = {
          placa: normalizarPlaca(d.placa),
          apelido: d.apelido || null,
          marca: d.marca || null,
          modelo: d.modelo || null,
          ano: d.ano ? Number(d.ano) : null,
          renavam: d.renavam ? apenasDigitos(d.renavam) : null,
          observacao: d.observacao || null,
        };
        if (tipo === 'trator') {
          dados.hodometro_inicial = d.hodometro_inicial ? lerInteiro(d.hodometro_inicial) : null;
          dados.carroceria = d.carroceria || null;
          dados.eixos = d.eixos ? Number(d.eixos) : null;
          dados.tanque_litros = d.tanque_litros ? lerInteiro(d.tanque_litros) : null;
          dados.licenciamento_ate = d.licenciamento_ate || null;
          dados.seguro_ate = d.seguro_ate || null;
        }
        else dados.acoplado_a = d.acoplado_a;

        if (novo) {
          const criado = await veiculos.criar({ ...dados, tipo });
          if (tipo === 'trator') {
            avisar('Caminhão cadastrado. Agora ative a placa.');
            tela.ir(`/veiculos/${criado.id}`, { substituir: true });
          } else {
            avisar('Carreta cadastrada.');
            tela.ir('/veiculos', { substituir: true });
          }
        } else {
          if (placaTravada) delete dados.placa;
          await veiculos.atualizar(v.id, dados, v);
          avisar('Alterações salvas.');
          tela.ir('/veiculos');
        }
      },
    });

    const blocos = [];
    if (!novo && tipo === 'trator') blocos.push(blocoPlaca(v, porV[v.id] ?? [], lista, porV, hoje));
    if (!novo && tipo === 'trator') {
      const minhas = lista.filter((c) => c.tipo === 'implemento' && c.acoplado_a === v.id && c.ativo);
      blocos.push(secao('Carretas acopladas', h('div', { class: 'lista' },
        minhas.map((c) => cartaoLink({ href: `#/veiculos/${c.id}`, simbolo: 'carreta', titulo: tituloVeiculo(c), linhas: [descricao(c)] })),
        minhas.length === 0 && h('p', { class: 'pequeno' }, 'Nenhuma carreta acoplada.'),
        gravavel && v.ativo && minhas.length < 3 && h('a', { class: 'botao secundario', href: `#/veiculos/novo?tipo=implemento&caminhao=${v.id}` }, '+ Acoplar carreta'))));
    }

    if (!novo && tipo === 'trator') blocos.push(await blocoManutencaoDoVeiculo(v));
    if (!novo) blocos.push(await blocoDespesas(v, lista, gravavel, hoje));

    trocar(corpo, 
      !novo && !v.ativo && faixa('Este veículo está desativado: não recebe lançamentos novos.', 'alerta'),
      ...blocos,
      secao(novo ? 'Dados' : 'Dados do veículo', form),
      !novo && gravavel && blocoDesativar(v, porV[v.id] ?? [], hoje),
    );
  })().catch((erro) => {
    console.error(erro);
    trocar(corpo, faixa(mensagemDeErro(erro), 'perigo'));
  });

  return casca({ titulo, voltar: '/veiculos', conteudo: [contaGravavel() ? null : faixaDaConta(), corpo] });
}

// ---------------------------------------------------------------------
// Despesas e consumo do veículo
// ---------------------------------------------------------------------
async function blocoDespesas(v, frota, gravavel, hoje) {
  const [lista, cats, pessoas, parc, envio, leituras] = await Promise.all([
    despesas.doVeiculo(v.id), categorias.todas(), colaboradores.listar(), parcelas.listar(), situacoesDeEnvio('lancamentos'), despesas.leituras(),
  ]);
  const doCaminhao = leituras.filter((l) => l.veiculo_id === v.id);
  const idsDiesel = new Set(cats.filter((c) => c.exige_hodometro).map((c) => c.id));
  const abastecimentos = doCaminhao.filter((l) => idsDiesel.has(l.categoria_id) && l.litros > 0);
  const recentes = abastecimentos.filter((a) => a.data >= somarDias(hoje, -90));
  const medio = consumoMedio(recentes.length >= 2 ? recentes : abastecimentos);
  const ultima = doCaminhao.filter((l) => l.hodometro !== null && l.hodometro !== undefined)
    .sort((a, b) => (a.data === b.data ? String(b.created_at ?? '').localeCompare(String(a.created_at ?? '')) : a.data < b.data ? 1 : -1))[0];
  const porConsumo = consumos(leituras, cats);
  const sufixo = `?veiculo=${v.id}&volta=${encodeURIComponent(`/veiculos/${v.id}`)}`;
  const trator = v.tipo === 'trator';

  return secao(trator ? 'Despesas e consumo' : 'Despesas', h('div', { class: 'lista' },
    trator && (medio || ultima) && h('dl', { class: 'linha-dados resumo' },
      medio && [
        h('dt', null, 'Consumo médio'), h('dd', null, `${numeroBR(medio.kmPorLitro, { casas: 2 })} km/l`),
        h('dt', null, 'Na conta'), h('dd', null, `${numeroBR(medio.km)} km · ${formatarLitros(medio.litros)} L`),
      ],
      ultima && [h('dt', null, 'Último km lançado'), h('dd', null, `${numeroBR(ultima.hodometro)} (${data(ultima.data)})`)]),
    trator && medio && h('p', { class: 'pequeno' },
      `Dos ${recentes.length >= 2 ? 'últimos 90 dias' : 'abastecimentos lançados'}: km do 1º ao último abastecimento ÷ litros abastecidos depois do 1º.`),
    lista.slice(0, 5).map((l) => cartaoDespesa(l, { cats, frota, pessoas, parc, envio, consumo: porConsumo.get(l.id), sufixo })),
    lista.length === 0 && h('p', { class: 'pequeno' }, 'Nenhuma despesa lançada neste veículo.'),
    lista.length > 5 && h('a', { class: 'botao secundario', href: `#/despesas?veiculo=${v.id}` }, 'Ver todas as despesas'),
    gravavel && v.ativo && h('div', { class: 'acoes-lado' },
      trator && h('a', { class: 'botao secundario', href: `#/despesas/nova${sufixo}&tipo=diesel` }, '+ Diesel'),
      h('a', { class: 'botao secundario', href: `#/despesas/nova${sufixo}` }, '+ Despesa'))),
  { contador: lista.length > 0 ? String(lista.length) : null });
}

// ---------------------------------------------------------------------
// Placa (licença) do caminhão
// ---------------------------------------------------------------------
function blocoPlaca(v, minhas, lista, porV, hoje) {
  const tela = nestaTela();
  const situacao = situacaoDaPlaca(minhas, hoje);
  // A regra do banco também olha a placa (pode ter rodado em outro cadastro).
  const jaTeve = situacao.jaTeve || Object.values(porV).flat().some((l) => l.placa === v.placa);
  const podeMexer = ehTitular() && contaGravavel() && v.ativo;
  let ocupado = false;

  const executar = async (acao, sucesso) => {
    if (ocupado) return;
    ocupado = true;
    for (const b of bloco.querySelectorAll('button')) b.disabled = true;
    try {
      await acao();
      avisar(sucesso);
      tela.renderizar();
    } catch (erro) {
      avisar(mensagemDeErro(erro), 'erro');
      for (const b of bloco.querySelectorAll('button')) b.disabled = false;
    } finally {
      ocupado = false;
    }
  };

  let status;
  if (situacao.ativa && situacao.encerraHoje) {
    status = faixa('A placa fica ativa até o fim de hoje e depois sai da assinatura.', 'alerta', 'Placa sendo retirada');
  } else if (situacao.ativa) {
    status = faixa(`Ativa desde ${data(situacao.desde)}. Conta na assinatura.`, 'ok', 'Placa ativa');
  } else {
    status = faixa('Sem placa ativa, o caminhão não recebe viagens nem despesas.', 'info', 'Placa não ativada');
  }

  const botoes = [];
  if (podeMexer && !situacao.ativa) {
    botoes.push(h('button', {
      class: 'botao cheio',
      type: 'button',
      onClick: async () => {
        const inicio = await dialogoAtivar(jaTeve, hoje);
        if (inicio) executar(() => licencas.incluir(v.id, inicio === hoje ? null : inicio), 'Placa ativada.');
      },
    }, 'Ativar placa'));
  }
  if (podeMexer && situacao.ativa && situacao.encerraHoje) {
    botoes.push(h('button', {
      class: 'botao cheio secundario',
      type: 'button',
      onClick: () => executar(() => licencas.incluir(v.id), 'A placa continua ativa.'),
    }, 'Manter a placa ativa'));
  }
  if (podeMexer && situacao.ativa && !situacao.encerraHoje) {
    const livres = lista.filter((t) => t.tipo === 'trator' && t.ativo && t.id !== v.id && !situacaoDaPlaca(porV[t.id] ?? [], hoje).ativa);
    botoes.push(
      h('button', {
        class: 'botao cheio secundario',
        type: 'button',
        onClick: async () => {
          const escolhido = await dialogoTrocar(v, livres);
          if (escolhido) executar(() => licencas.trocar(v.id, escolhido), 'Placa trocada.');
        },
      }, 'Trocar por outro caminhão'),
      h('button', {
        class: 'botao cheio perigo',
        type: 'button',
        onClick: async () => {
          const ok = await confirmar({
            titulo: `Retirar a placa ${formatarPlaca(v.placa)}?`,
            texto: 'Ela fica ativa até o fim de hoje e sai da assinatura. Depois disso, o caminhão não recebe lançamentos com data posterior a hoje. Religar depois só vale a partir do dia em que religar.',
            sim: 'Retirar placa',
            perigo: true,
          });
          if (ok) executar(() => licencas.retirar(v.id), 'Placa retirada.');
        },
      }, 'Retirar placa'),
    );
  }

  const historico = [...minhas].sort((a, b) => (a.inicio < b.inicio ? 1 : -1));
  const MOTIVO = { inclusao: 'ativada', troca: 'troca', implantacao: 'implantação', retirada: 'retirada' };

  const bloco = secao('Placa', h('div', { class: 'lista' },
    status,
    !ehTitular() && h('p', { class: 'pequeno' }, 'Só o titular da conta ativa, troca ou retira placas.'),
    botoes.length > 0 && h('div', { class: 'acoes' }, botoes),
    historico.length > 0 && h('details', null,
      h('summary', { class: 'pequeno' }, `Histórico (${historico.length})`),
      h('ul', { class: 'pequeno' }, historico.map((l) =>
        h('li', null, `${data(l.inicio)} até ${l.fim ? data(l.fim) : 'hoje'} · ${MOTIVO[l.motivo_inicio] ?? l.motivo_inicio}${l.motivo_fim ? ` → ${MOTIVO[l.motivo_fim] ?? l.motivo_fim}` : ''}`))))));
  return bloco;
}

function dialogoAtivar(jaTeve, hoje) {
  return dialogo((fechar) => {
    const entrada = h('input', { type: 'date', id: 'ativar-inicio', name: 'inicio', value: hoje, max: hoje, 'aria-describedby': 'dica-inicio' });
    const erro = h('p', { class: 'erro-campo', role: 'alert' });
    return [
      h('h2', null, 'Ativar placa'),
      jaTeve
        ? h('p', null, 'Esta placa já teve licença antes, então ela volta a valer a partir de hoje.')
        : h('div', { class: 'campo' },
          h('label', { for: 'ativar-inicio' }, 'Ativa desde'),
          entrada,
          h('p', { class: 'dica', id: 'dica-inicio' }, 'Na primeira ativação você pode escolher uma data anterior, para lançar as viagens e despesas antigas.'),
          erro),
      h('p', null, `Durante o teste grátis não há cobrança. Depois, a assinatura é de ${precoPorMes()}.`),
      h('div', { class: 'acoes' },
        h('button', {
          class: 'botao cheio',
          type: 'button',
          onClick: () => {
            const inicio = jaTeve ? hoje : entrada.value;
            if (!inicio) {
              erro.textContent = 'Escolha a data.';
              return;
            }
            if (inicio > hoje) {
              erro.textContent = 'A data não pode ser no futuro.';
              return;
            }
            fechar(inicio);
          },
        }, 'Ativar placa'),
        h('button', { class: 'botao cheio secundario', type: 'button', onClick: () => fechar(null) }, 'Cancelar')),
    ];
  });
}

function dialogoTrocar(v, livres) {
  return dialogo((fechar) => {
    if (livres.length === 0) {
      return [
        h('h2', null, 'Trocar a placa'),
        h('p', null, 'Para trocar, cadastre primeiro o caminhão que vai entrar no lugar deste. Ele precisa estar sem placa ativa.'),
        h('div', { class: 'acoes' },
          h('a', { class: 'botao cheio', href: '#/veiculos/novo?tipo=trator', onClick: () => fechar(null) }, 'Cadastrar caminhão'),
          h('button', { class: 'botao cheio secundario', type: 'button', onClick: () => fechar(null) }, 'Cancelar')),
      ];
    }
    const escolha = h('select', { name: 'entra', 'aria-label': 'Caminhão que entra' },
      h('option', { value: '' }, 'Escolha o caminhão que entra'),
      livres.map((t) => h('option', { value: t.id }, `${formatarPlaca(t.placa)}${t.apelido ? ` · ${t.apelido}` : ''}`)));
    const erro = h('p', { class: 'erro-campo', role: 'alert' });
    return [
      h('h2', null, `Trocar a placa ${formatarPlaca(v.placa)}`),
      h('p', null, 'O caminhão que sai fica com a placa até hoje; o que entra começa hoje. A assinatura continua com a mesma quantidade de placas. Só é permitida 1 troca por mês.'),
      h('div', { class: 'campo' }, escolha, erro),
      h('div', { class: 'acoes' },
        h('button', {
          class: 'botao cheio',
          type: 'button',
          onClick: () => {
            if (!escolha.value) {
              erro.textContent = 'Escolha o caminhão.';
              return;
            }
            fechar(escolha.value);
          },
        }, 'Trocar placa'),
        h('button', { class: 'botao cheio secundario', type: 'button', onClick: () => fechar(null) }, 'Cancelar')),
    ];
  });
}

// ---------------------------------------------------------------------
// Desativar, reativar, excluir
// ---------------------------------------------------------------------
function blocoDesativar(v, minhas, hoje) {
  const tela = nestaTela();
  const situacao = v.tipo === 'trator' ? situacaoDaPlaca(minhas, hoje) : { ativa: false, jaTeve: false };
  const podeExcluir = v.tipo === 'implemento' || !situacao.jaTeve;

  const alternar = async () => {
    if (v.ativo && situacao.ativa && !situacao.encerraHoje) {
      avisar('Retire a placa antes de desativar o caminhão.', 'erro');
      return;
    }
    const ok = await confirmar({
      titulo: v.ativo ? 'Desativar este veículo?' : 'Reativar este veículo?',
      texto: v.ativo
        ? 'Ele sai das listas de lançamento, mas o histórico continua guardado. Dá para reativar depois.'
        : v.tipo === 'implemento'
          ? 'A carreta volta a aceitar despesas. Ela precisa estar acoplada a um caminhão ativo.'
          : 'O caminhão volta para a lista. Para lançar, ative a placa de novo.',
      sim: v.ativo ? 'Desativar' : 'Reativar',
    });
    if (!ok) return;
    try {
      await veiculos.atualizar(v.id, { ativo: !v.ativo });
      avisar(v.ativo ? 'Veículo desativado.' : 'Veículo reativado.');
      tela.renderizar();
    } catch (erro) {
      avisar(mensagemDeErro(erro), 'erro');
    }
  };

  const excluir = async () => {
    const ok = await confirmar({
      titulo: 'Excluir este cadastro?',
      texto: 'Use só para cadastros feitos por engano. O registro vai para a lixeira.',
      sim: 'Excluir',
      perigo: true,
    });
    if (!ok) return;
    try {
      await veiculos.excluir(v.id);
      avisar('Cadastro excluído.');
      tela.ir('/veiculos', { substituir: true });
    } catch (erro) {
      avisar(mensagemDeErro(erro), 'erro');
    }
  };

  return h('div', { class: 'acoes' },
    h('button', { class: 'botao cheio secundario', type: 'button', onClick: alternar }, v.ativo ? 'Desativar veículo' : 'Reativar veículo'),
    podeExcluir && h('button', { class: 'botao texto', type: 'button', onClick: excluir }, 'Excluir cadastro feito por engano'));
}
